import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { toast } from "sonner";
import { Factory } from "lucide-react";
import { TYPE_ICON, STATUS_OPTS, StatusChip } from "./shared";

export function RelevanceTab({ activations, canEdit, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const d = {};
    (activations?.catalog || []).forEach((e) => { d[e.code] = e.status || "not_yet_screened"; });
    setDraft(d);
  }, [activations]);

  const grouped = useMemo(() => {
    const g = {};
    (activations?.catalog || []).filter((e) => e.scope === 1).forEach((e) => {
      (g[e.source_type] = g[e.source_type] || []).push(e);
    });
    return g;
  }, [activations]);

  if (!activations) return <div className="card-flat p-6 text-sm text-muted-foreground">Loading catalog…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const items = (activations.catalog || []).map((e) => ({ code: e.code, status: draft[e.code] }));
      await api.post("/ghg/activations/bulk", { items });
      toast.success("Relevance screen saved");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  const ss = activations.screening_status;
  const ssMeta = ss === "screened" ? { l: "Screening complete", c: "text-[#00E65B]" }
    : ss === "in_progress" ? { l: "Screening in progress", c: "text-[#FFB800]" }
    : { l: "Not yet screened", c: "text-[#FFB800]" };

  return (
    <div className="space-y-4">
      <div className="card-flat p-4 flex items-center justify-between flex-wrap gap-3" data-testid="ghg-relevance-header">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Mark each shared-catalog source as <b>active</b> (material — you capture it) or
          <b> screened — not material</b> (assessed and excluded, with an audit trail). Nothing disappears —
          unselected sources stay visible as screened.
        </p>
        <span className={`label-eyebrow text-[10px] ${ssMeta.c}`} data-testid="ghg-screening-status">{ssMeta.l}</span>
      </div>

      {(activations.scope1_source_types || []).map((meta) => {
        const Icon = TYPE_ICON[meta.key] || Factory;
        const entries = grouped[meta.key] || [];
        return (
          <div key={meta.key} className="card-flat p-4" data-testid={`ghg-relevance-type-${meta.key}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon className="h-4 w-4 text-accent" />
              <span className="font-display font-semibold text-sm">{meta.label}</span>
              <Badge variant="secondary" className="text-[9px] font-mono">Scope 1</Badge>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">{meta.desc}</p>
            <div className="space-y-2">
              {entries.map((e) => (
                <div key={e.code} className="flex items-center justify-between gap-3 py-1.5 border-b border-border last:border-0" data-testid={`ghg-relevance-entry-${e.code}`}>
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{e.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{e.description}</div>
                    {e.note && <div className="text-[10px] text-[#FFB800] mt-0.5">{e.note}</div>}
                  </div>
                  {canEdit ? (
                    <Select value={draft[e.code] || "not_yet_screened"} onValueChange={(v) => setDraft((p) => ({ ...p, [e.code]: v }))}>
                      <SelectTrigger className="w-52 shrink-0" data-testid={`ghg-relevance-select-${e.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <StatusChip status={draft[e.code] || "not_yet_screened"} testid={`ghg-relevance-status-${e.code}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {canEdit && (
        <div className="flex justify-end">
          <Button variant="primary" onClick={save} disabled={saving} data-testid="ghg-relevance-save">
            {saving ? "Saving…" : "Save relevance screen"}
          </Button>
        </div>
      )}
    </div>
  );
}
